/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchAppConfig, updateAppConfig } from "../../../api";
import { RootState } from "../../store";
import { Edits, AppConfig, LoadStatus } from "../../../types";

export interface AppState {
  app?: AppConfig;
  appEdits: Edits<AppConfig>;
  appLoadStatus: LoadStatus;
}

const initialState: AppState = {
  app: undefined,
  appEdits: {},
  appLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadApp = createAsyncThunk(
  "app/loadApp",
  async (_, thunkAPI): Promise<AppConfig> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await fetchAppConfig(state.login.accessToken);
  }
);

export const updateApp = createAsyncThunk(
  "app/updateApp",
  async (data: Partial<AppConfig>, thunkAPI): Promise<AppConfig> => {
    const state = thunkAPI.getState() as RootState;
    const user = state.login.user;
    const token = state.login.accessToken;
    if (!user || !token) throw new Error("login required");
    return await updateAppConfig(token, data);
  }
);

export const revertApp = createAsyncThunk(
  "app/revertApp",
  async (args, thunkAPI): Promise<AppConfig> => {
    const state = thunkAPI.getState() as RootState;
    const user = state.login.user;
    const token = state.login.accessToken;
    if (!user || !token) throw new Error("login required");
    const edits = state.app.appEdits;
    if (
      !edits ||
      !edits.oldData ||
      `${JSON.stringify(edits.oldData)}` === `${JSON.stringify(edits.newData)}`
    ) {
      throw new Error("no changes to revert");
    }
    return await updateAppConfig(token, edits.oldData);
  }
);

export const appSlice = createSlice({
  name: "app",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load app
      .addCase(loadApp.pending, (state) => {
        state.appLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadApp.fulfilled, (state, action) => {
        state.app = action.payload;
        state.appLoadStatus = LoadStatus.DONE;
        state.appEdits = {
          oldData: action.payload,
          newData: undefined,
        };
      })
      .addCase(loadApp.rejected, (state) => {
        state.appLoadStatus = LoadStatus.FAILED;
      })
      // add or update app
      .addCase(updateApp.fulfilled, (state, action) => {
        state.app = action.payload;
        state.appEdits.newData = action.payload;
      })
      // revert app changes
      .addCase(revertApp.fulfilled, (state, action) => {
        state.app = action.payload;
        state.appEdits.newData = undefined;
      });
  },
});

export default appSlice.reducer;
